export type ApiError = Error & { status?: number; code?: string };

/**
 * Machine-readable error codes the UI branches on. HTTP status alone is
 * ambiguous — ORG_SLUG_TAKEN and USER_ALREADY_IN_ORGANIZATION are both 409, and
 * the two terminal auth codes are indistinguishable from an expired token.
 * Never branch on `message`: those strings are user-facing copy and are
 * localized server-side.
 *
 * ORG_CONTEXT_MISSING (401) and NO_ORGANIZATION_CONTEXT (403) are different
 * things despite the near-identical names, and must never be merged:
 *   - 401 ORG_CONTEXT_MISSING is terminal. The token itself is wrong, so
 *     re-authenticating returns the identical token and retrying is an infinite
 *     redirect loop. Handled in authInterceptor by routing to /no-organization.
 *   - 403 NO_ORGANIZATION_CONTEXT is not terminal. The token was fine, but this
 *     particular write cannot be scoped to an organization — what a superadmin
 *     gets from per-organization writes, by design. Must not sign the user out.
 */
export type ApiErrorCode =
  | 'PLATFORM_ROLE_CLAIM_INVALID'
  | 'ORG_CONTEXT_MISSING'
  | 'CROSS_ORG_ACCESS_DENIED'
  | 'NO_ORGANIZATION_CONTEXT'
  | 'ACCOUNT_NOT_FOUND'
  | 'USER_NOT_AUTHORIZED'
  | 'TEAM_INSUFFICIENT_PERMISSIONS'
  | 'ORG_NAME_REQUIRED'
  | 'ORG_SLUG_REQUIRED'
  | 'ORG_SLUG_INVALID'
  | 'ORG_SLUG_RESERVED'
  | 'ORG_SLUG_TAKEN'
  | 'ORG_FIELDS_NOT_ALLOWED'
  | 'ORGANIZATION_NOT_FOUND'
  | 'ORG_NOT_ACTIVE'
  | 'ORG_ALREADY_ACTIVE'
  | 'USER_ALREADY_IN_ORGANIZATION'
  | 'PLATFORM_INVITATION_DUPLICATE_PENDING'
  | 'PLATFORM_INVITATION_ALREADY_ACCEPTED'
  | 'PLATFORM_INVITATION_ALREADY_EXPIRED'
  | 'PLATFORM_INVITATION_ALREADY_REVOKED'
  | 'TEAM_INVITATION_ALREADY_ACCEPTED'
  // Org-admin mutations: deleting a member, and promoting or demoting one. Only
  // CANNOT_DELETE_LAST_ADMIN and ADMIN_NOT_FOUND_FOR_OWNERSHIP_TRANSFER are
  // branched on today; the rest are listed because there is no generated client,
  // so this union is the only place the contract is written down. Their server
  // copy is already actionable and is relayed verbatim.
  | 'CANNOT_DELETE_LAST_ADMIN'
  | 'CANNOT_DEMOTE_LAST_ADMIN'
  | 'PLATFORM_ROLE_CHANGE_NOT_PERMITTED'
  | 'PLATFORM_ROLE_CHANGED_CONCURRENTLY'
  | 'ORG_ADMIN_MUTATION_IN_PROGRESS'
  | 'ORG_ADMIN_LOCK_UNAVAILABLE'
  | 'ORG_ADMIN_COUNT_UNAVAILABLE'
  | 'ADMIN_NOT_FOUND_FOR_OWNERSHIP_TRANSFER'
  // Document uploads: prepare-upload rejects an unsupported extension with this
  // code (a non-scoped 403). MIME mismatches surface only as a message, so the
  // helper below falls back to getApiErrorMessage for those.
  | 'FORBIDDEN_FILE_EXTENSION'
  // Requirement evidence. REQUIREMENT_STEP_ALREADY_SATISFIED is a 409 raised when a completed
  // recurring obligation is re-submitted before its renewal window opens — indistinguishable from
  // any other conflict by status alone, which is exactly why it carries a code. The two date codes
  // are separate from EVIDENCE_VALIDATION_FAILED so the message can go on the date field rather
  // than the form.
  | 'REQUIREMENT_STEP_ALREADY_SATISFIED'
  // Ticking an evidence-backed action complete is refused: it would start a recurring step's expiry
  // window with no evidence submitted. Such actions complete only via the evidence endpoint.
  | 'ACTION_REQUIRES_EVIDENCE'
  | 'EVIDENCE_ATTESTATION_DATE_REQUIRED'
  | 'EVIDENCE_ATTESTATION_DATE_IN_FUTURE';

export const getApiErrorStatus = (error: unknown): number | undefined => {
  if (typeof error !== 'object' || error === null) return undefined;

  const candidate = error as {
    status?: unknown;
    response?: { status?: unknown };
  };

  if (typeof candidate.status === 'number') return candidate.status;
  if (typeof candidate.response?.status === 'number') {
    return candidate.response.status;
  }

  return undefined;
};

export const getApiErrorMessage = (
  error: unknown,
  fallback: string
): string => {
  if (typeof error === 'object' && error !== null) {
    const candidate = error as {
      message?: unknown;
      response?: { data?: { message?: unknown } };
    };
    const responseMessage = candidate.response?.data?.message;

    if (typeof responseMessage === 'string') return responseMessage;
    if (Array.isArray(responseMessage)) return responseMessage.join(', ');
    if (typeof candidate.message === 'string') return candidate.message;
  }

  return fallback;
};

export const getApiErrorCode = (error: unknown): string | undefined => {
  if (typeof error !== 'object' || error === null) return undefined;

  const candidate = error as {
    code?: unknown;
    response?: { data?: { code?: unknown } };
  };
  const responseCode = candidate.response?.data?.code;

  if (typeof responseCode === 'string') return responseCode;
  if (typeof candidate.code === 'string') return candidate.code;

  return undefined;
};

export const isApiErrorCode = (
  error: unknown,
  ...codes: ApiErrorCode[]
): boolean => {
  const expectedCodes: string[] = codes;
  const code = getApiErrorCode(error);

  return code !== undefined && expectedCodes.includes(code);
};

/**
 * True when an upload was rejected for its file type. Matches the backend's
 * `FORBIDDEN_FILE_EXTENSION` code; MIME mismatches carry no code, so callers
 * should still surface `getApiErrorMessage(error)` for those.
 */
export const isUploadFileTypeError = (error: unknown): boolean =>
  isApiErrorCode(error, 'FORBIDDEN_FILE_EXTENSION');

export const createApiError = (error: unknown, fallback: string): ApiError => {
  const apiError = new Error(getApiErrorMessage(error, fallback)) as ApiError;
  apiError.status = getApiErrorStatus(error);
  apiError.code = getApiErrorCode(error);

  return apiError;
};

export const normalizeApiError = (
  error: unknown,
  fallback = 'Request failed'
): ApiError => {
  if (error instanceof Error && typeof getApiErrorStatus(error) === 'number') {
    return error as ApiError;
  }

  return createApiError(error, fallback);
};

export const isServerErrorStatus = (status?: number | null): status is number =>
  typeof status === 'number' && status >= 500;
